import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { CheckCircleIcon, ServerIcon, SettingsIcon, TrashIcon } from '@/components/icons';
import { StatusIndicator } from '@/components/StatusIndicator';
import { originHealthMap } from '@/config/statusMaps';
import { formatOriginUrl, pluralize } from '@/utils/format';
import type { OriginGroup } from '@/types/originGroup';

/**
 * Row actions of the origin groups table
 */
export type OriginGroupRowAction = 'test' | 'edit' | 'delete';

export interface OriginGroupsTableProps {
  originGroups: OriginGroup[];
  isLoading: boolean;
  /** Shown in the empty state when set (user may create origin groups) */
  onAddFirst?: () => void;
  getActions: (og: OriginGroup) => {
    test: boolean;
    edit: boolean;
    delete: boolean;
    deleteBlocked: boolean;
  };
  isBusy: (action: OriginGroupRowAction, id: string) => boolean;
  onAction: (action: OriginGroupRowAction, og: OriginGroup) => void;
}

const COLUMN_COUNT = 7;

/**
 * Number of upstream servers (the primary address counts as one)
 */
function formatMemberCount(og: OriginGroup): string {
  const count = og.members?.length ?? 0;
  return count === 0 ? '1 server' : pluralize(count, 'server');
}

/**
 * Origin groups table
 */
export function OriginGroupsTable({
  originGroups,
  isLoading,
  onAddFirst,
  getActions,
  isBusy,
  onAction,
}: OriginGroupsTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Name</TableHeader>
          <TableHeader>Origin</TableHeader>
          <TableHeader>Servers</TableHeader>
          <TableHeader>TLS</TableHeader>
          <TableHeader>Health</TableHeader>
          <TableHeader>Domains</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {originGroups.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<ServerIcon />}
                message={
                  onAddFirst
                    ? 'No origin groups yet. Create your first origin group to define backend servers.'
                    : 'No origin groups have been created yet.'
                }
                action={onAddFirst && <Button onClick={onAddFirst}>Add Origin Group</Button>}
              />
            </TableCell>
          </TableRow>
        ) : (
          originGroups.map((og) => {
            const actions = getActions(og);

            return (
              <TableRow key={og.id}>
                <TableCell>
                  <div className="origin-group-name">
                    <span className="origin-group-name-text">{og.name}</span>
                    {og.description && <span className="origin-group-description">{og.description}</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <code className="origin-group-address">{formatOriginUrl(og)}</code>
                </TableCell>
                <TableCell>{formatMemberCount(og)}</TableCell>
                <TableCell>
                  {og.enable_tls ? (
                    <span className="origin-tls origin-tls-enabled">HTTPS</span>
                  ) : (
                    <span className="origin-tls origin-tls-disabled">HTTP</span>
                  )}
                </TableCell>
                <TableCell>
                  <StatusIndicator status={og.health_status} map={originHealthMap} baseClassName="origin-health" />
                </TableCell>
                <TableCell>
                  <span className="origin-group-domain-count">{og.domain_count}</span>
                </TableCell>
                <TableCell align="right">
                  <div className="origin-group-actions">
                    {actions.test && (
                      <IconButton
                        className="origin-group-action"
                        icon={<CheckCircleIcon />}
                        label={`Test connection to ${og.name}`}
                        title="Test Connection"
                        isBusy={isBusy('test', og.id)}
                        busyContent={<span className="origin-group-action-loading">...</span>}
                        onClick={() => onAction('test', og)}
                      />
                    )}
                    {actions.edit && (
                      <IconButton
                        className="origin-group-action"
                        icon={<SettingsIcon />}
                        label={`Edit ${og.name}`}
                        title="Edit"
                        onClick={() => onAction('edit', og)}
                      />
                    )}
                    {actions.delete && (
                      <IconButton
                        className="origin-group-action origin-group-action-danger"
                        icon={<TrashIcon />}
                        label={`Delete ${og.name}`}
                        title={actions.deleteBlocked ? 'Cannot delete: has domains' : 'Delete'}
                        disabled={actions.deleteBlocked}
                        onClick={() => onAction('delete', og)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
